/** Numbers, windows and bucket labels of the 사용량 screens.
 *
 *  Buckets are UTC days on every window but the shortest, where they are
 *  hourly instants. Both are shown in UTC because that is what the aggregates
 *  are keyed by, with the Seoul clock beside them: the UTC day rolls over at
 *  09:00 KST, so a reader here would otherwise do a nine-hour subtraction to
 *  know whether a number covers last night or this morning. */

import { formatAbsolute, formatKstClock, formatRelative, formatUtcHour, fromUnixMs, utcHourOf } from '@/lib/time'

/** Window presets. 1 is the last 24 hours, because the 7/30/90 lumps made the
 *  most common question, "what happened today?", the one the screen could not
 *  answer. */
export const WINDOWS = [1, 3, 7, 30, 90]

/** The window the list opens on: a month of history is the point of a
 *  portfolio view. */
export const LIST_DAYS = 30
/** The window an app's page opens on: it is opened to see what the app is
 *  doing now, and a 30-day sum buries today inside it. */
export const DETAIL_DAYS = 1

export const PLATFORMS = ['ios', 'android', 'macos']

export function windowLabel(days: number): string {
  return days === 1 ? '24시간' : `${days}일`
}

export function windowHint(days: number): string {
  return days === 1
    ? '지금까지의 24시간. UTC 하루는 서울 09:00에 시작해서 오전 내내 거의 비어 있다.'
    : `오늘까지 UTC ${days}일`
}

/** "ios" → "iOS". Anything unknown is shown as it is. */
export function platformLabel(p: string): string {
  switch (p) {
    case 'ios':
      return 'iOS'
    case 'android':
      return 'Android'
    case 'macos':
      return 'macOS'
    default:
      return p
  }
}

/** `platforms` arrives as GROUP_CONCAT(DISTINCT platform): "ios", "android" or
 *  "android,ios". Split, never substring-match: a later value like
 *  "ios-visionos" would make `includes('ios')` quietly wrong. */
export function platformsOf(raw: string): string[] {
  return raw
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean)
}

export const fmtNum = (n: number): string => Math.round(n).toLocaleString('ko-KR')

export function fmtDuration(ms: number): string {
  if (!ms || ms < 0) return '—'
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}초`
  const m = Math.floor(s / 60)
  const rest = s % 60
  if (m < 60) return rest ? `${m}분 ${rest}초` : `${m}분`
  const h = Math.floor(m / 60)
  return `${h}시간 ${m % 60}분`
}

export function fmtPct(v: number, digits = 1): string {
  if (!isFinite(v)) return '—'
  return `${(v * 100).toFixed(digits)}%`
}

/** Views per user keeps one decimal: the point of the column is the fraction,
 *  and rounding it to an integer turns 1.5 into 1. */
export function fmtPer(n: number): string {
  if (!isFinite(n) || n <= 0) return '0'
  return n.toFixed(1)
}

/** Screen enters with no exit at this share or more make dwell meaningless:
 *  an enter with no exit adds zero milliseconds by design, so the average
 *  comes from a small, self-selected minority of visits. */
export const UNPAIRED_WARN = 0.2

/** "3분 전 · 10. 17. 13:35" for an analytics `last_event_at` (unix ms). */
export function freshnessText(lastEventAt: number): string {
  const iso = fromUnixMs(lastEventAt)
  if (!iso) return ''
  return `${formatRelative(iso)} · ${formatAbsolute(iso)}`
}

/** A bucket label is either a UTC day (`2026-09-01`) or, on the 24-hour
 *  window, the RFC3339 instant an hour bucket starts at. */
export function isHourBucket(label: string): boolean {
  return label.includes('T')
}

/** `2026-09-01` → `09-01`, `2026-09-02T14:00:00Z` → `14시`. The day form is
 *  sliced, never parsed: `new Date('2026-09-01')` is UTC midnight and would
 *  render as the previous day west of Greenwich. */
export function shortDay(day: string): string {
  if (isHourBucket(day)) return `${utcHourOf(day)}시`
  return day.length >= 10 ? day.slice(5) : day
}

/** `2026-09-02T14:00:00Z` → `09-02 14:00 UTC · 23:00 KST`. */
export function hourLabel(iso: string): string {
  return `${formatUtcHour(iso)} UTC · ${formatKstClock(iso)} KST`
}

/** A UTC day names a span shifted nine hours in Seoul, which is why "today"
 *  looks empty all morning. Said once, in the header tooltip, not on every
 *  bar. */
export function dayLabel(day: string): string {
  const end = new Date(new Date(`${day}T00:00:00Z`).getTime() + 24 * 3600_000)
  return `${day} UTC = KST ${day.slice(5)} 09:00 → ${end.toISOString().slice(5, 10)} 09:00`
}

/** The label of one bucket, whichever kind it is. */
export function bucketLabel(label: string): string {
  return isHourBucket(label) ? hourLabel(label) : `${label} UTC`
}

/** The window as a line of text. */
export function rangeLabel(buckets: { day: string }[]): string {
  const first = buckets[0].day
  const last = buckets[buckets.length - 1].day
  if (isHourBucket(first)) return `${hourLabel(first)} → ${hourLabel(last)}`
  return `${first} → ${last} UTC`
}

export function rangeTitle(buckets: { day: string }[]): string {
  const first = buckets[0].day
  if (isHourBucket(first)) return '시간 버킷은 시작하는 UTC 시각으로 부른다. KST는 UTC+9다.'
  return `날짜는 UTC다. UTC 하루는 서울 09:00에 넘어간다.\n${dayLabel(first)}`
}

/** The query string that carries the window and platform to the other page,
 *  where `fallback` is THAT page's default. Written out even when it matches
 *  this page's default: the two pages disagree about what "no ?days=" means,
 *  so dropping it is how a choice gets lost in transit. */
export function analyticsQuery(days: number, platform: string, fallback: number): string {
  const q = new URLSearchParams()
  if (days !== fallback) q.set('days', String(days))
  if (platform) q.set('platform', platform)
  const s = q.toString()
  return s ? `?${s}` : ''
}

/** "14:00–15:00 UTC", the hour-of-day a drill-down is scoped to. */
export function utcHourText(hour: number): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(hour)}:00–${pad(hour + 1)}:00 UTC`
}
